import { Link } from 'react-router-dom';
import { REQUEST_MAX_AGE_SECONDS } from '@pryme/protocol';
import { Alert, Icon, relativeTime } from '@pryme/ui';
import { CopyField } from '../components/CodeBlock';
import { useServiceOutlet } from '../components/ServiceLayout';

export function Security() {
  const { service: s } = useServiceOutlet();

  return (
    <>
      <section className="p-card" aria-labelledby="k-h">
        <h2 id="k-h">Key status</h2>
        <div className="p-table-wrap">
          <table className="p-table">
            <thead><tr><th>Key</th><th>Purpose</th><th>Status</th><th>Last used</th></tr></thead>
            <tbody>
              {s.keys.map((k) => (
                <tr key={k.id}>
                  <td><code>{k.id}</code><div className="p-small p-muted">{k.fingerprint}</div></td>
                  <td className="p-small">{k.direction === 'A' ? 'Pryme signs: responses to your requests' : 'You sign: /token, /v1/verify, /v1/authorizations/*'}</td>
                  <td><span className={`p-status ${k.status === 'active' ? 'ok' : 'bad'}`}>{k.status === 'active' ? 'Active' : 'Revoked'}</span></td>
                  <td>{k.direction === 'B' ? relativeTime(k.last_used_at) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="p-small" style={{ margin: '0.75rem 0 0' }}><Link to="../credentials">Rotate or revoke keys <Icon name="arrowRight" size={13} /></Link></p>
      </section>

      <section className="p-card" aria-labelledby="aud-h">
        <h2 id="aud-h">Audience and replay policy</h2>
        <CopyField label="Allowed audience for your signed requests" value={s.apiAudience} hint="Pryme rejects any signature whose audience field differs (wrong_audience / invalid_signature)." />
        <dl className="p-kv">
          <dt>Recipient audience</dt><dd>Tokens Pryme issues for you (state, mandates, decisions) carry <code>aud = {s.id}</code>. Reject anything else.</dd>
          <dt>Timestamp window</dt><dd>±{REQUEST_MAX_AGE_SECONDS} s around Pryme's clock. Keep your servers NTP-synced.</dd>
          <dt>Nonces</dt><dd>Single-use per service, retained for {REQUEST_MAX_AGE_SECONDS * 2} s; a reused nonce is rejected with <code>replay_detected</code> and logged as <code>signature.replay</code>. Nonces are only consumed after the signature verifies, so attackers cannot burn yours.</dd>
          <dt>Signed fields</dt><dd>principal, key ID, method, audience, exact path+query, SHA-256 digest of the exact body bytes, timestamp, nonce.</dd>
          <dt>Ignored headers</dt><dd><code>Host</code>, <code>Origin</code>, <code>Referer</code>, <code>X-Requester-Host</code> have no authentication power.</dd>
          <dt>Responses</dt><dd>Every successful signed response carries <code>pryme-response-signature</code>, signed with Pryme's verification key and bound to your nonce, status and body digest.</dd>
        </dl>
      </section>

      <section className="p-card" aria-labelledby="on-h">
        <h2 id="on-h">Delegated actions: authorized online by Pryme</h2>
        <p className="p-small">
          A person&rsquo;s permissions belong to them, not to your service. Before you act on an agent&rsquo;s request, call <code>identity.authorize()</code>{' '}
          (<code>POST /v1/authorizations/check</code>): Pryme checks the person&rsquo;s current permission, scope, limits, budget and revocation, asks the person when needed,
          and fails closed when it cannot be reached. A mandate proof shows who delegated what; it is not a live authorization on its own. The agent&rsquo;s permission covers
          only the exact operation the agent signed (<code>Pryme-Intent</code>, forwarded by the SDK), once; a check your service makes on its own can only ask the person.
          Whether you then perform the action is your own decision.
        </p>
      </section>

      <section className="p-card" aria-labelledby="env-h">
        <h2 id="env-h">Environment separation</h2>
        <Alert tone={s.environment === 'production' ? 'info' : 'warn'}>
          This is a <strong>{s.environment}</strong> service.
        </Alert>
        <ul className="p-small">
          <li><strong>Sandbox</strong> has a separate trust root. Its people are Idura MitID <em>test</em> users or offline test-provider users, <strong>never real MitID-verified people</strong>. Claims carry <code>assurance.environment = "sandbox"</code>, and identity status is reported as <code>active_sandbox</code>.</li>
          <li><strong>Production</strong> requires HTTPS redirect URIs on a verified domain of your organization (<Link to="/domains">Domains</Link>, in Production) and accepts only production identity evidence. A production service never silently uses sandbox evidence: mismatched environments fail with <code>issuer_not_verified</code>.</li>
          <li>The environment is the deployment: services you create while the switch is on Sandbox are sandbox services, and production services are created in Production. Sandbox and production have separate organizations, services and keys; never reuse production credentials in development.</li>
          <li>Configure the SDK with <code>environment: '{s.environment}'</code>; in production mode it rejects sandbox assurance as human verification.</li>
        </ul>
      </section>
    </>
  );
}
